import angular from "angular";
import { vrmCoreModule } from "@/angular/modules/vrm.core";
import { flushFormatCache } from "@/angular/libs/sprintf";
import {
    currentVersion,
} from "@/version/update-detector";
import { vrmConfig } from "@/config";
import { localeEvents } from "@/angular/modules/vrm.locales";
import { localeMeta } from "@/languages/meta";
import moment from 'moment';
import { RELOAD_PAGE_EVENT } from '@/version/update-detector';

const dependencies = [vrmCoreModule.name];

if (typeof vrmConfig === "undefined") {
    // Throw error if no config is set
    throw new Error(
        "No config file loaded, make sure dist/config/config.js exists"
    );
}

angular
    .module("vrm", dependencies)
    .config(CoreConfig)
    .run(CoreRun);

//HACK reconfigure Google Maps in run phase
var gmapProvider = null;

function redirectToLogin($state, $location, localStorageService) {
    const relativeUrl = $location.url();
    localStorageService.set("login.returnUrl", relativeUrl);
    localStorageService.set(
        "login.returnAddedAt",
        Math.floor(Date.now() / 1000)
    );
    $state.go("login");
}

/* @ngInject */
function CoreConfig(
    vrmConfig,
    $logProvider,
    $compileProvider,
    $httpProvider,
    $stateProvider,
    jwtOptionsProvider,
    localStorageServiceProvider,
    vcRecaptchaServiceProvider,
    $provide,
    $rootScopeProvider,
    $qProvider
) {
    // We need 20 for users with a lot of sites
    $rootScopeProvider.digestTtl(20);
    // Cache busting
    $provide.decorator("$templateFactory", [
        "$delegate",
        function ($delegate) {
            var fromUrl = angular.bind($delegate, $delegate.fromUrl);
            $delegate.fromUrl = function (url, params) {
                if (
                    url !== null &&
                    angular.isDefined(url) &&
                    angular.isString(url)
                ) {
                    url += url.indexOf("?") === -1 ? "?" : "&";
                    url += "b=" + currentVersion;
                }
                return fromUrl(url, params);
            };
            return $delegate;
        }
    ]);

    // Enable or disable debugging
    $logProvider.debugEnabled(vrmConfig.debug);
    $compileProvider.debugInfoEnabled(vrmConfig.debugInfoEnabled);

    // Setup where to store the WebToken
    localStorageServiceProvider.setPrefix(vrmConfig.localStoragePrefix);

    // automatically get the token from localStorage
    jwtOptionsProvider.config({
        authHeader: "X-Authorization",
        tokenGetter: [
            "localStorageService",
            "$state",
            "jwtHelper",
            "$log",
            "$location",
            function (localStorageService, $state, jwtHelper, $log, $location) {
                // Get token
                var token = localStorageService.get("token");
                if (new URLSearchParams(window.location.search).has('demoShare')) {
                    return vrmConfig.demoShareToken;
                }

                // check if token is expired
                if (token && jwtHelper.isTokenExpired(token)) {
                    localStorageService.remove("token");
                    if ($state.current.name.indexOf("login") != 0) {
                        redirectToLogin($state, $location, localStorageService);
                    }
                    $log.info("Token expired");
                    return null;
                }

                return token;
            }
        ],
        whiteListedDomains: [vrmConfig.apiDomain, vrmConfig.reportApiDomain],
        unauthenticatedRedirector: [
            "localStorageService",
            "$state",
            "$location",
            function (localStorageService, $state, $location) {
                const metadata = $state.current.data || {};
                if (!metadata.skipLoginCheck) {
                    redirectToLogin($state, $location, localStorageService);
                }
            }
        ],

    });

    // ties the token insert to the $http calls
    $httpProvider.interceptors.push("jwtInterceptor");

    //
    $stateProvider.state("base", {
        name: "base",
        abstract: true,
        views: {
            base: {
                template: require("@/angular/views/layout/with-nav.html"),
                controller: "BaseController"
            }
        }
    });

    vcRecaptchaServiceProvider.setDefaults({
        key: vrmConfig.recaptchaKey,
        size: "invisible"
    });

    // whitelist for allowed hrefs
    $compileProvider.aHrefSanitizationTrustedUrlList(/^\s*(https?|s?ftp|mailto|tel|file|victron-connect):/);
}

/* @ngInject */
function CoreRun(
    $http,
    $location,
    $log,
    $rootScope,
    $state,
    $timeout,
    $window,
    ApiService,
    VrmLangService,
    WhitelabelService,
    amMoment,
    authManager,
    localStorageService,
    locale,
    vrmConfig,
    vrmNotificationType,
    vrmNotificationsService,
    NotificationsService,
    VrmModalService,
    NOTIFICATION_CHANNELS,
    NOTIF_LS_KEYS,
) {
    // Page visibility changes
    let pageVisibilityInterval = null;
    let pageVisibility = true;
    const isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    const createPageVisibilityInterval = () =>{
        clearInterval(pageVisibilityInterval)
        pageVisibilityInterval = setInterval(() => {
            if (window.__skipUpdateChecks) {
                return;
            }
            console.log("[PAGE VISIBILITY] Page is invisible.");
            pageVisibility = false;
            $rootScope.$broadcast("page-visibility-change", false);
            window.dispatchEvent(new CustomEvent('page-visible', {'detail': { 'isVisible': false }}))
            clearInterval(pageVisibilityInterval);
        }, (isFirefox ? 2 : 10) * 1000); // firefox has aggressive throttling, so we need to check more often
    }


    function handlePageVisibilityChange() {
        if (document.hidden) {
            createPageVisibilityInterval();
        } else {
            if (pageVisibilityInterval) {
                clearInterval(pageVisibilityInterval);
            }
            if (!pageVisibility) {
                console.log('[PAGE VISIBILITY] Page is visible again.')
                $rootScope.$broadcast('page-visibility-change', true);
                window.dispatchEvent(new CustomEvent('page-visible', {'detail': { 'isVisible': true }}))
                pageVisibility = true;
            }
        }
    }

        document.addEventListener(
        "visibilitychange",
        handlePageVisibilityChange,
        false
    );

    $rootScope.vrmConfig = vrmConfig;
    // Set default amMoment locale to EN
    amMoment.changeLocale(localeMeta.getMomentJsLocale(localeMeta.main));

    WhitelabelService.setWhitelabel(false);

    $rootScope.redirectToDashboardDone = false;

    authManager.redirectWhenUnauthenticated();
    $rootScope.$on("$stateChangeStart", function (
        event,
        toState,
        toParams,
        fromState,
        fromParams,
        options
    ) {
        // Fix memory leak in sprintf.js
        flushFormatCache();


        // on page change
        window.dispatchEvent(new CustomEvent(RELOAD_PAGE_EVENT));


        const metadata = toState.data || {};
        const isLoggedIn = ApiService.isLoggedIn();
        const isShare = ApiService.isShare();

        // Protect routes that require login
        const requireLogin = !metadata.skipLoginCheck;
        const sharedCanAccess = metadata.sharedCanAccess; // tells wether a route can be accessed if it is not a shared route

        //shareCanAccess == null then redirect
        // if new version is available -> reload the page

        if (isShare && sharedCanAccess == null) {
            // alert( "redirect")
            $state.go("base.installation-overview", {
                returnUrl: toState.name,
                returnParams: toParams
            });

            locale.ready('core').then(function () {
                vrmNotificationsService.add({ type: vrmNotificationType.ERROR, message: locale.getString("core.not_allowed_shared_user") });
            });

            event.preventDefault();
        }

        if (requireLogin && !isLoggedIn) {
            redirectToLogin($state, $location, localStorageService);
            event.preventDefault();
        }
    });

    $rootScope.$on(localeEvents.localeChanges, function (event, lang) {
        amMoment.changeLocale(localeMeta.getMomentJsLocale(lang));
    });

    // Apply user language after login / user data load
    $rootScope.$on("user:loaded", (event, user) => {
        if (user.language) {
            VrmLangService.setLangLocal(user.language);
        }
    });

    // Restore local language
    let localLanguage = localStorageService.get("language");
    if (localLanguage) {
        VrmLangService.setLangLocal(localLanguage);
    }

    $rootScope.$on("$stateChangeSuccess", function () {
        // Scroll to top
        document.body.scrollTop = document.documentElement.scrollTop = 0;
    });

    $rootScope.$on("$locationChangeSuccess", function () {
        //Google Tag manager
        if (!vrmConfig.debug) {
            $log.info(
                "GTM :: Logging event: " +
                $location.url() +
                " " +
                document.getElementsByTagName("title")[0].innerHTML
            );
            window.dataLayer.push({
                event: "VirtualPageview",
                user_id: ApiService.getUserId(),
                virtualPageURL: $location.url(),
                virtualPageTitle: document.getElementsByTagName("title")[0]
                    .innerHTML
            });
        }
    });

    const handleUserLoadedOnMobile = () => {
        if (
            window.webkit &&
            window.webkit.messageHandlers &&
            window.webkit.messageHandlers.handleUserLoaded
        ) {
            window.webkit.messageHandlers.handleUserLoaded.postMessage({});
        }

        if (window.Android && window.Android.handleUserLoaded) {
            window.Android.handleUserLoaded();
        }
    };

    const handleAllowedEvent = async user => {
        if (!ApiService.isLoggedIn()) {
            return;
        }

        const deviceToken = await NotificationsService.getDeviceToken();
        const deviceName = NotificationsService.getDeviceName();
        const tokenType = NotificationsService.getTokenType();
        const response = await NotificationsService.getNotificationChannels(user.id, { deviceToken });
        const { data: channels } = response.data;

        if (channels.current == null) {
            $rootScope.$broadcast(
                "notification-loading",
                NOTIFICATION_CHANNELS.mobile
            );
            NotificationsService.handleSubscriptionToChannel(NOTIFICATION_CHANNELS.mobile, user.id, { deviceToken, deviceName, tokenType })
                .then(function () {
                    // update toggle on preferences/notification
                    $rootScope.$broadcast(
                        "notification-update",
                        NOTIFICATION_CHANNELS.mobile
                    );
                })
                .catch(function (err) {
                    $log.error(err);
                    // subscription error
                    $rootScope.$broadcast(
                        "notification-update",
                        NOTIFICATION_CHANNELS.mobile
                    );
                });
        }
    };

    const handleDisallowedEvent = async user => {
        if (!ApiService.isLoggedIn()) {
            return;
        }

        const deviceToken = await NotificationsService.getDeviceToken();
        const response = await NotificationsService.getNotificationChannels(user.id, { deviceToken });
        const { data: channels } = response.data;

        if (channels.current != null) {
            $rootScope.$broadcast(
                "notification-loading",
                NOTIFICATION_CHANNELS.mobile
            );
            NotificationsService.unsubscribeDeviceFromChannel(
                user.id,
                channels.current.idBinding,
            ).finally(() => {
                $rootScope.$broadcast(
                    "notification-update",
                    NOTIFICATION_CHANNELS.mobile
                );
            });
        }
    };

    const updateBindingTag = (user, binding) => {
        NotificationsService.handleSubscriptionToChannel(
            binding.idNotificationChannel,
            user.id,
            {
                deviceName: NotificationsService.getDeviceName(),
                deviceToken: binding.deviceToken,
                tokenType: binding.tokenType,
            }
        );
    };

    let storedAllowedEventCb = null;
    let storedDisallowedEventCb = null;
    $rootScope.$on("user:loaded", (event, user) => {
        if (!vrmConfig.firebase.apiKey || !vrmConfig.firebase.messagingSenderId) return;

        // firebase messaging requires service workers
        if ("serviceWorker" in navigator) {
            NotificationsService.initFirebaseMessaging();
        }

        // handle notification permission changes in browsers
        if ("permissions" in navigator) {
            navigator.permissions
                .query({ name: "notifications" })
                .then(browserPermissions => {
                    browserPermissions.addEventListener("change", (browserPermissions) => {
                        if (browserPermissions.currentTarget.state === "denied") {
                            const idBinding = localStorageService.get(`CURRENT_ID_TOKEN-${user.id}`);
                            if (!idBinding) {
                                return;
                            }
                            $rootScope.$broadcast(
                                "notification-loading",
                                NOTIFICATION_CHANNELS.desktop
                            );

                            NotificationsService.unsubscribeDeviceFromChannel(
                                user.id,
                                idBinding
                            ).finally(() => {
                                $rootScope.$broadcast(
                                    "notification-update",
                                    NOTIFICATION_CHANNELS.desktop
                                );
                            });
                        }
                    });
                });
        }


        // handle enabled notifications triggered from mobile apps
        window.mobileNotificationsAllowedEvent = new Event(
            "mobileNotificationsAllowedEvent"
        );
        // bind event only once
        if (storedAllowedEventCb == null) {
            window.addEventListener(
                "mobileNotificationsAllowedEvent",
                () => {
                    storedAllowedEventCb && storedAllowedEventCb();
                }
            );
        }
        storedAllowedEventCb = () => {
            console.log("mobile notifications allowed");
            if (NotificationsService.checkMobileWrapper()) {
                handleAllowedEvent(user);
            } else {
                console.error(
                    "mobileNotificationsAllowedEvent triggered in non-mobile environment"
                );
            }
        };

        // handle disabled notifications
        window.mobileNotificationsDisallowedEvent = new Event(
            "mobileNotificationsDisallowedEvent"
        );
        if (storedDisallowedEventCb == null) {
            window.addEventListener(
                "mobileNotificationsDisallowedEvent",
                () => {
                    storedDisallowedEventCb && storedDisallowedEventCb();
                }
            );
        }
        storedDisallowedEventCb = () => {
            console.log("mobile notifications disabled");
            handleDisallowedEvent(user);
        };


        NotificationsService.processPushNotificationsRequest(false).then(() => {
            // browser supports notifications and all service variables where initialized
        }).catch(() => {
            // browser does not support notifications or we are in the pwa
        }).finally(async () => {
            const deviceToken = await NotificationsService.getDeviceToken();
            let lastUpdatedDate = moment();
            NotificationsService.getNotificationChannels(user.id, { deviceToken }).then(response => {
                if (response.status !== 200) {
                    return;
                }
                const { data: bindings } = response.data;

                if (bindings.current !== null) {
                    localStorageService.set(`CURRENT_ID_TOKEN-${user.id}`, bindings.current.idBinding);
                    lastUpdatedDate = moment(bindings.current.updatedTime);
                    const diffDays = moment().diff(lastUpdatedDate, 'days');
                    if (diffDays > 1) {
                        updateBindingTag(user, bindings.current);
                    }
                } else {
                    const currentId = localStorageService.get(`CURRENT_ID_TOKEN-${user.id}`);
                    const found = bindings.other.find(b => b.idBinding == currentId);
                    if (!found || !currentId) {
                        localStorageService.set(`CURRENT_ID_TOKEN-${user.id}`, null);
                        return;
                    }
                    $rootScope.$broadcast(
                        "notification-loading",
                        NOTIFICATION_CHANNELS.desktop
                    );
                    NotificationsService.unsubscribeDeviceFromChannel(
                        user.id,
                        currentId
                    ).finally(() => {
                        localStorageService.set(`CURRENT_ID_TOKEN-${user.id}`, null);
                        $rootScope.$broadcast(
                            "notification-update",
                            NOTIFICATION_CHANNELS.desktop
                        );
                    });
                }
            }).finally(() => {
                // send event to mobile devices to trigger user loaded flow
                handleUserLoadedOnMobile();
            });
        });
    });

    const apnsProtection = setInterval(() => {
        const fcmToken = window.localStorage.getItem(NOTIF_LS_KEYS.FCM_TOKEN);
        const apnToken = window.localStorage.getItem(NOTIF_LS_KEYS.APN_TOKEN);
        if (fcmToken && apnToken && ApiService.getUserId()) {
            // had both tokens, only apn no longer needed
            handleAllowedEvent({ id: ApiService.getUserId() });
            window.localStorage.removeItem(NOTIF_LS_KEYS.APN_TOKEN);
            clearInterval(apnsProtection);
        } else if (!apnToken) {
            // does not have an apn token, so either has none or is already migrated to fcm
            clearInterval(apnsProtection);
        }
        // if no condition matches wait for fcm token
    }, 500);
}
